import { get } from 'lodash';
import {
  ApiResponse,
  CCApiResponse,
  IAssembly,
  IDaypart,
  IDefaultAssembly,
  ILayout,
  ILayoutType,
  ILocation,
  ILocationGroup,
  ILocationTag,
  IMappedLocationGroupDaypart,
  IScheduleAssembly,
  IViewChannel,
  IViewLocation,
  IViewLocationLayoutsWithScheduleAssemblies,
  IViewLocations,
} from 'src/interfaces';
import { IScheduleOverride } from 'src/interfaces/ScheduleOverride';
import { getScreenConfigurationLabel, lang } from 'src/utils';
import { formatTimestamp } from 'src/utils/date';

export const mapViewLocationsByGroups = (
  response: CCApiResponse,
): IViewLocation[] => {
  return response.data.map((location: ApiResponse) =>
    mapViewLocation(location),
  );
};

const mapViewLocation = (location: ApiResponse) => {
  return location as IViewLocation;
};

export const mapLocations = (
  response: CCApiResponse,
  viewLocations: IViewLocation[] = [],
  layoutTypes: ILayoutType[] = [],
): ILocation[] => {
  return response.data.map((location: ApiResponse) =>
    mapLocation(
      location,
      get(response, 'includes.locationGroups.data', []),
      viewLocations.filter(
        (item) => item.locations.location_code === location.location_code,
      ),
      layoutTypes,
      get(response, 'includes.locationTags.data', []),
      get(response, 'includes.locationOwnerTags.data', []),
    ),
  );
};

export const mapLocation = (
  location: ApiResponse,
  locationGroups: ILocationGroup[] = [],
  viewLocations: IViewLocation[] = [],
  layoutTypes: ILayoutType[] = [],
  locationTags: ILocationTag[] = [],
  locationOwnerTags: ILocationTag[] = [],
) => {
  const locationGroupIds = get(location, 'location_group_id', []);
  const locationTagIds = get(location, 'location_tag_id', []);
  const locationOwnerTagIds = get(location, 'location_owner_tag_id', []);

  return {
    ...location,
    locationGroups: locationGroups
      .filter((item) => locationGroupIds.includes(item.id))
      .map((item) => ({
        ...item,
        layoutType: layoutTypes.find(
          (layoutType) => layoutType.id === item.layout_type_id,
        ),
      })),
    locationTags: locationTags.filter((tag) => locationTagIds.includes(tag.id)),
    locationOwnerTags: locationOwnerTags.filter((tag) =>
      locationOwnerTagIds.includes(tag.id),
    ),
    hq: get(viewLocations, '[0].hq'),
    has_override: !!viewLocations.some(
      (location) => location.has_override === true,
    ),
  } as ILocation;
};

export const mapViewLocations = (
  viewLocationResponse: CCApiResponse,
  viewChannels: IViewChannel[],
  locationGroupsResponse: ILocationGroup[],
): IViewLocations => {
  const locationGroups = locationGroupsResponse.map((item) => ({
    ...item,
    screenConfigurationLabel: getScreenConfigurationLabel(
      viewChannels.find((item2) => item2.channel_name === item.channel_name)
        ?.screens ?? [],
    ),
    locations: [],
  }));

  const locationTags = get(
    viewLocationResponse,
    'includes.locationTags.data',
    [],
  ) as ILocationTag[];

  const locationOwnerTags = get(
    viewLocationResponse,
    'includes.locationOwnerTags.data',
    [],
  ) as ILocationTag[];

  const alreadyAddedUnassignedLocations = new Set<string>();
  const defaultLocationCodes = new Set(
    (viewLocationResponse.data as IViewLocation[])
      .filter(
        (data) =>
          data.locationGroups.is_default &&
          data.locations.location_code != null,
      )
      .map((data) => data.locations.location_code),
  );

  return viewLocationResponse.data.reduce(
    (acc: IViewLocations, location: IViewLocation) => {
      const newLocation = {
        id: location.id,
        name: location.hq.name,
        address_line_1: location.hq.address_line_1,
        address_line_2: location.hq.address_line_2,
        city: location.hq.city,
        config: location.locations.config,
        country: location.hq.country,
        has_assembly_variation: location.has_assembly_variation,
        has_override: location.has_override,
        postal_code: location.hq.postal_code,
        location_code: location.locations.location_code,
        region: location.hq.region,
        store_number: location.hq.store_number,
        timezone: location.hq.timezone,
        channels: location.hq.channels,
        zone: location.hq.zone,
        subzone: location.hq.subzone,
        location_tag_id: location.locations.location_tag_id,
        locationTags: locationTags.filter((item) =>
          location.locations.location_tag_id.includes(item.id),
        ),
        location_owner_tag_id: location.locations.location_owner_tag_id,
        locationOwnerTags: locationOwnerTags.filter((item) =>
          location.locations.location_owner_tag_id.includes(item.id),
        ),
      };

      const hasChannels = location.hq.channels?.length > 0;
      const locationCode = location.locations.location_code;
      const isNotAlreadyAdded =
        !alreadyAddedUnassignedLocations.has(locationCode);
      const isNotDefaultCode = !defaultLocationCodes.has(locationCode);
      const matchingViewLocations = viewLocationResponse.data.filter(
        (data: IViewLocation) =>
          data.locations.location_code === locationCode &&
          data.hq.channels?.includes(data.locationGroups.channel_name),
      );
      const hasIncompleteChannelMatch =
        matchingViewLocations.length !== location.hq.channels?.length;

      const isUnassigned =
        (!location.locationGroups?.id && hasChannels && isNotAlreadyAdded) ||
        (hasChannels &&
          !location.locationGroups.is_default &&
          isNotDefaultCode &&
          isNotAlreadyAdded &&
          hasIncompleteChannelMatch);

      if (isUnassigned) {
        acc.unassignedLocations.push(newLocation);
        alreadyAddedUnassignedLocations.add(locationCode);
      }

      const locationGroup = acc.locationGroups.find(
        (item) => item.id === location?.locationGroups?.id,
      );

      if (locationGroup) {
        locationGroup.locations.push(newLocation);
      }

      return acc;
    },
    {
      unassignedLocations: [],
      locationGroups: locationGroups,
    } as IViewLocations,
  );
};

export const mapLocationScheduleAssembly = (
  scheduleAssemblies: IScheduleAssembly[],
  assemblies: IAssembly[],
  dayparts: IDaypart[],
  scheduleOverrides: IScheduleOverride[],
): IMappedLocationGroupDaypart[] => {
  return scheduleAssemblies.map((scheduleAssembly: IScheduleAssembly) => {
    const matchScheduleOverrides = scheduleOverrides.filter(
      (item) => item.schedule_assembly_id === scheduleAssembly.id,
    );

    return {
      id: scheduleAssembly.id,
      startDate: scheduleAssembly.start_date || '',
      endDate: scheduleAssembly.end_date || '',
      scheduledOrDefault: scheduleAssembly?.end_date
        ? 'Scheduled'
        : 'Set as default',
      assemblyName:
        assemblies.find((item) => item.id === scheduleAssembly.assembly_id)
          ?.name || '',
      assemblyScheduleDate:
        formatTimestamp(scheduleAssembly?.modified_at || '') || '',
      daypartName:
        dayparts.find((item) => item.id === scheduleAssembly.daypart_id)
          ?.name || '',
      assemblyData:
        assemblies.find((item) => item.id === scheduleAssembly.assembly_id) ||
        ({} as IAssembly),
      daypartData:
        dayparts.find((item) => item.id === scheduleAssembly.daypart_id) ||
        ({} as IDaypart),
      locationCode: scheduleAssembly.location_code || '',
      scheduleOverrides: matchScheduleOverrides,
      locationGroupId: scheduleAssembly.location_group_id,
    };
  });
};
export const mapLocationDefaultAssembly = (
  scheduleAssemblies: IDefaultAssembly[],
  assemblies: IAssembly[],
  dayparts: IDaypart[],
): IMappedLocationGroupDaypart[] => {
  const matchScheduleOverrides: any = [];

  return scheduleAssemblies.map((scheduleAssembly: IDefaultAssembly) => {
    const startDate =
      scheduleAssembly.start_date || scheduleAssembly.created_at || '';

    return {
      id: scheduleAssembly.id,
      startDate,
      endDate: scheduleAssembly.valid_to || '',
      scheduledOrDefault: 'Default',
      assemblyName:
        assemblies.find((item) => item.id === scheduleAssembly.assembly_id)
          ?.name || '',
      assemblyScheduleDate: scheduleAssembly?.modified_at || '',
      daypartName:
        dayparts.find((item) => item.id === scheduleAssembly.daypart_id)
          ?.name || '',
      assemblyData:
        assemblies.find((item) => item.id === scheduleAssembly.assembly_id) ||
        ({} as IAssembly),
      daypartData:
        dayparts.find((item) => item.id === scheduleAssembly.daypart_id) ||
        ({} as IDaypart),
      scheduleOverrides: matchScheduleOverrides,
      locationGroupId: scheduleAssembly.location_group_id,
      status: scheduleAssembly?.status,
    };
  });
};

export const mapScheduleAssemblies = (
  response: CCApiResponse,
): IScheduleAssembly[] => {
  return response.data.map((item: ApiResponse) =>
    mapScheduleAssembly(item, response.includes),
  );
};

const mapScheduleAssembly = (
  scheduleAssembly: ApiResponse,
  includes?: ApiResponse,
) => {
  const assemblies = get(includes, 'assemblies.data', []) as IAssembly[];
  const dayparts = get(includes, 'dayparts.data', []) as IDaypart[];

  return {
    ...scheduleAssembly,
    assembly: assemblies.find(
      (item) => item.id === scheduleAssembly.assembly_id,
    ),
    daypart: dayparts.find((item) => item.id === scheduleAssembly.daypart_id),
  } as IScheduleAssembly;
};

export const mapDefaultAssemblies = (
  response: CCApiResponse,
): IDefaultAssembly[] => {
  return response.data.map((item: ApiResponse) =>
    mapDefaultAssembly(item, response.includes),
  );
};

const mapDefaultAssembly = (
  scheduleAssembly: ApiResponse,
  includes?: ApiResponse,
) => {
  const assemblies = get(includes, 'assemblies.data', []) as IAssembly[];
  const dayparts = get(includes, 'dayparts.data', []) as IDaypart[];
  const locationGroup = get(
    includes,
    'locationGroups.data',
    [],
  ) as ILocationGroup[];

  return {
    ...scheduleAssembly,
    assembly: assemblies.find(
      (item) => item.id === scheduleAssembly.assembly_id,
    ),
    daypart: dayparts.find((item) => item.id === scheduleAssembly.daypart_id),
    locationGroup: locationGroup.find(
      (item) => item.id === scheduleAssembly.location_group_id,
    ),
    start_date: scheduleAssembly.start_date || null,
  } as IDefaultAssembly;
};

export const mapViewLocationsByScheduleAssemblies = (
  response: CCApiResponse,
): IViewLocationLayoutsWithScheduleAssemblies[] => {
  const assemblies: IAssembly[] = get(response, 'includes.assemblies.data', []);
  const layouts: ILayout[] = get(
    response,
    'includes.assemblies.includes.layouts.data',
    [],
  );

  return response.data.map((item: ApiResponse) => {
    const assemblyData = assemblies.find(
      (assembly) => assembly.id === item.schedule_assembly.assembly_id,
    );

    const scheduledOrDefault = item.schedule_assembly.end_date
      ? lang.scheduled
      : lang.setAsDefault;

    return {
      ...item,
      scheduledOrDefault,
      assembly: {
        ...assemblyData,
        layout: layouts.find((layout) => layout.id === assemblyData?.layout_id),
      },
      schedule_assembly: {
        ...item.schedule_assembly,
        id: item.id.split('-')[0],
        location_group_id: item.id.split('-')[1],
      },
    } as IViewLocationLayoutsWithScheduleAssemblies;
  });
};

export const parsePageFromFilters = (list: string[]) => {
  const found = list.find((f) => f.startsWith('page='));
  const num = found ? parseInt(found.split('=')[1], 10) : 1;

  return Number.isNaN(num) ? 1 : num;
};

export const setOrReplaceParam = (
  list: string[],
  key: string,
  value: string | number,
) => {
  const keyPrefix = `${key}=`;
  let replaced = false;
  const updated = list.map((f) => {
    if (f.startsWith(keyPrefix)) {
      replaced = true;

      return `${key}=${value}`;
    }

    return f;
  });
  if (!replaced) updated.push(`${key}=${value}`);

  return updated;
};

export const getPagesToFetch = (totalPages: number, queryParams: string[]) => {
  const startPage = parsePageFromFilters(queryParams);
  const pages: number[] = [];
  for (let p = 1; p <= totalPages; p += 1) {
    if (p !== startPage) pages.push(p);
  }

  return pages;
};
